import { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { getCustomerInsightsCMS, getCustomerWalletDetails } from "../config/Services";

const EMPTY_INSIGHTS = {
  totalOrders: null,
  loyaltyPoints: null,
  averageBasket: null,
  lastOrderedAt: null,
  walletBalance: null,
};

export const formatInsightAmount = (value) =>
  Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

export const formatInsightCurrency = (value) =>
  value === null || value === undefined ? "NA" : `₹${formatInsightAmount(value)}`;

export const formatInsightCount = (value) =>
  value === null || value === undefined ? "NA" : value;

export const InsightSpinner = ({ small }) => (
  <CircularProgress size={small ? 14 : 18} sx={{ color: "#2a71ff" }} />
);

export const formatInsightDate = (raw) => {
  if (!raw) return "First order in progress";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d
    .toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(",", " ·");
};

/**
 * Fetches the customer insights tiles from the two customer-service endpoints.
 * Analytics (total orders / loyalty points / average basket / last ordered) is keyed by uidx,
 * wallet balance by the retail customer id.
 */
const useCustomerInsights = ({ uidx, id }) => {
  const [insights, setInsights] = useState(EMPTY_INSIGHTS);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!uidx && !id) {
      setInsights(EMPTY_INSIGHTS);
      setInsightsLoading(false);
      return;
    }
    const orgId = localStorage.getItem("orgId") || "";
    const locId = localStorage.getItem("locId") || "";
    const reqId = ++requestRef.current;
    setInsightsLoading(true);

    Promise.allSettled([
      uidx ? getCustomerInsightsCMS(uidx) : Promise.resolve(null),
      id ? getCustomerWalletDetails(id, orgId) : Promise.resolve(null),
    ])
      .then(([analyticsRes, walletRes]) => {
        // a slower response for a previously typed number must not overwrite the current one
        if (reqId !== requestRef.current) return;
        const analytics =
          analyticsRes?.status === "fulfilled"
            ? analyticsRes.value?.data?.customerAnalyticsData
            : null;
        const wallet = walletRes?.status === "fulfilled" ? walletRes.value?.data?.data : null;
        setInsights({
          totalOrders: analytics?.totalOrders ?? null,
          loyaltyPoints: wallet?.totalAvailablePoint ?? null,
          averageBasket: analytics?.averageOrderValue ?? null,
          lastOrderedAt: analytics?.lastVisitDate ?? null,
          walletBalance: wallet?.walletBalance ?? null,
        });
      })
      .finally(() => {
        if (reqId === requestRef.current) setInsightsLoading(false);
      });
  }, [uidx, id]);

  return { insights, insightsLoading };
};

export default useCustomerInsights;
